import React, { useEffect, useRef, useState } from 'react'
import propTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { useHistory, useLocation } from 'react-router'
import Text from 'ui/Text'
import {
    HEADER_MOBILE_HEIGHT,
    HEADER_DESKTOP_HEIGHT,
    TOPBAR_MOBILE_HEIGHT,
    TOPBAR_DESKTOP_HEIGHT,
} from 'configs/ui'
import BACKGROUNDS from 'themes/dk/backgrounds'
import device from 'utils/mediaQueries'
import Icon from 'ui/Icon'
import left from 'icons/left.svg'
import right from 'icons/right.svg'
import { detectIpad, detectMobile } from 'common/adapters/DeviceDetectAdapter'

const ControlWrapper = styled.div`
    display: flex;
    position: absolute;
    width: 40px;
    height: 100%;
    top: 0;
    left: 0;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    background: linear-gradient(
        to right,
        rgba(0, 0, 0, 0.95) 60%,
        transparent 100%
    );

    ${({ right }) =>
        right &&
        css`
            right: 0;
            left: auto;
            background: linear-gradient(
                to left,
                rgba(0, 0, 0, 0.95) 60%,
                transparent 100%
            );
        `};
`

const Wrapper = styled.div`
    position: fixed;
    width: 100%;
    height: auto;
    background: rgb(0, 0, 0, 0.8);
    transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
    z-index: 8;
    top: calc(${TOPBAR_DESKTOP_HEIGHT} + ${HEADER_DESKTOP_HEIGHT});

    @media screen and ${device.md} {
        top: calc(${TOPBAR_MOBILE_HEIGHT} + ${HEADER_MOBILE_HEIGHT});
    }

    ${({ show }) =>
        show
            ? css`
                  transform: translateY(0);
                  opacity: 1;
              `
            : css`
                  transform: translateY(-100%);
                  opacity: 0;
                  pointer-events: none;
              `};

    .hasHover &:hover {
        background: rgb(0, 0, 0, 1);
    }
`

const getSlideWidth = () => (detectMobile() && !detectIpad() ? 120 : 150)

const ItemsWrapper = styled.div`
    position: relative;
    width: 100%;
`

const ScrollContainer = styled.div`
    padding: 0 40px;
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    touch-action: pan-x;
    user-select: none;

    &::-webkit-scrollbar {
        display: none;
    }

    &::before,
    &::after {
        content: '';
        margin: auto;
    }
`
const Slide = styled.div`
    position: relative;
    cursor: pointer;
    flex-shrink: 0;
    width: ${getSlideWidth()}px;
    ${Text} {
        padding: 20px 0;
        transition: all 0.3s ease-in-out;
        &:after {
            transition: all 0.5s ease;
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 4px;
            background-color: ${BACKGROUNDS.primary};
            opacity: ${({ active }) => (active ? 1 : 0)};
        }
    }

    .hasHover &:hover {
        ${Text} {
            filter: drop-shadow(0 0 6px ${BACKGROUNDS.primary});
        }
    }

    @media screen and ${device.md} {
        ${Text} {
            font-size: 13px;
        }
    }
`

const PrevArrow = (props) => (
    <ControlWrapper onClick={props.onClick}>
        <Icon width="8px" height="12px" src={left} />
    </ControlWrapper>
)
const NextArrow = (props) => (
    <ControlWrapper right onClick={props.onClick}>
        <Icon width="8px" height="12px" src={right} />
    </ControlWrapper>
)

const SubMenu = ({ show, items = [] }) => {
    const [visible, setVisible] = useState(show)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const history = useHistory()
    const location = useLocation()
    const scrollRef = useRef(null)
    const slideRefs = useRef([])
    const dragState = useRef({ isDown: false, startX: 0, scrollLeft: 0 })

    const handleClick = (path) => {
        if (isDragging) return
        setVisible(false)
        history.push(path)
    }

    const handleMouseDown = (e) => {
        if (!scrollRef.current) return
        dragState.current.isDown = true
        dragState.current.startX = e.pageX - scrollRef.current.offsetLeft
        dragState.current.scrollLeft = scrollRef.current.scrollLeft
    }

    const handleMouseUp = () => {
        if (!scrollRef.current) return
        dragState.current.isDown = false
        setTimeout(() => setIsDragging(false), 0)
    }

    const handleMouseLeave = () => {
        if (!scrollRef.current) return
        dragState.current.isDown = false
        setTimeout(() => setIsDragging(false), 0)
    }

    const handleMouseMove = (e) => {
        if (!dragState.current.isDown || !scrollRef.current) return
        e.preventDefault()
        const x = e.pageX - scrollRef.current.offsetLeft
        const walk = (x - dragState.current.startX) * 1.5
        if (Math.abs(walk) > 5) {
            setIsDragging(true)
        }
        scrollRef.current.scrollLeft = dragState.current.scrollLeft - walk
    }

    const scrollLeftHandler = () => {
        if (scrollRef.current) {
            const newPosition =
                scrollRef.current.scrollLeft - getSlideWidth() * 2
            scrollRef.current.scrollTo({
                left: Math.max(0, newPosition),
                behavior: 'smooth',
            })
        }
    }

    const scrollRightHandler = () => {
        if (scrollRef.current) {
            const container = scrollRef.current
            const maxScroll = container.scrollWidth - container.clientWidth
            const newPosition = container.scrollLeft + getSlideWidth() * 2
            container.scrollTo({
                left: Math.min(maxScroll, newPosition),
                behavior: 'smooth',
            })
        }
    }

    useEffect(
        () => {
            setVisible(show)
        },
        [show]
    )

    // Check scroll position to show/hide arrows
    const updateScrollButtons = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
            setCanScrollLeft(scrollLeft > 0)
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)
        }
    }

    useEffect(
        () => {
            const container = scrollRef.current
            if (!container) return

            // Initial check after render
            const timer = setTimeout(updateScrollButtons, 200)

            // Update on scroll
            container.addEventListener('scroll', updateScrollButtons)
            window.addEventListener('resize', updateScrollButtons)

            return () => {
                clearTimeout(timer)
                container.removeEventListener('scroll', updateScrollButtons)
                window.removeEventListener('resize', updateScrollButtons)
            }
        },
        [items, show]
    )

    // Scroll to active item when location changes or menu becomes visible
    useEffect(
        () => {
            if (!show || !items.length) return

            const scrollToActive = () => {
                const activeIndex = items.findIndex((item) =>
                    location.pathname.startsWith(item.path)
                )
                const container = scrollRef.current
                const slide = slideRefs.current[activeIndex]

                if (activeIndex >= 0 && slide && container) {
                    const slideOffsetLeft = slide.offsetLeft
                    const slideWidth = slide.offsetWidth
                    const containerWidth = container.offsetWidth
                    // Center the active slide in the container
                    const scrollPosition =
                        slideOffsetLeft - containerWidth / 2 + slideWidth / 2
                    container.scrollLeft = Math.max(0, scrollPosition)
                }
            }

            // Delay to ensure container is rendered
            const timer = setTimeout(scrollToActive, 150)
            return () => clearTimeout(timer)
        },
        [location.pathname, items, show]
    )

    return (
        <Wrapper show={visible && items.length > 0}>
            <ItemsWrapper>
                {canScrollLeft && <PrevArrow onClick={scrollLeftHandler} />}
                <ScrollContainer
                    ref={scrollRef}
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseLeave}
                    onMouseMove={handleMouseMove}
                >
                    {items.map((item, index) => {
                        return (
                            <Slide
                                key={index}
                                ref={(el) => {
                                    slideRefs.current[index] = el
                                }}
                                onClick={() => handleClick(item.path)}
                                active={location.pathname.startsWith(item.path)}
                            >
                                <Text align={'center'}>{item.name}</Text>
                            </Slide>
                        )
                    })}
                </ScrollContainer>
                {canScrollRight && <NextArrow onClick={scrollRightHandler} />}
            </ItemsWrapper>
        </Wrapper>
    )
}

SubMenu.propTypes = {
    show: propTypes.bool.isRequired,
    items: propTypes.array,
}

export default SubMenu
